import { createContext, useReducer, useMemo, useEffect, useContext, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { IReservationsContext, IReservationsProviderProps } from "./ReservationsProviderTypes"
import { RESERVATIONS_INITIAL_CONTEXT, RESERVATIONS_INITIAL_STATE, } from "./ReservationsProviderConstants"
import { NOTIFICATION_MESSAGE_TYPES } from "types/notification"

import useGetReservations from "api/querries/reservations/useGetReservations"
import useGetReservation from "api/querries/reservations/useGetReservation"
import useGetCarWashSchedule from "api/querries/reservations/useGetCarWashSchedule"
import useUpdateReservationBoxMutation from "api/mutations/reservations/useUpdateReservationBox"
import { IGetReservationsQueryParams } from "types/reservation"
import useDebounce from "hooks/useDebounce"
import { useUI } from "providers/UIProvider"
import useRecalculateReservationAmountMutation from "api/mutations/reservations/useRecalculateReservationAmount"
import useUpdateReservationInfoMutation from "api/mutations/reservations/useUpdateReservationInfo"
import useCancelReservationMutation from "api/mutations/reservations/useCancelReservation"
import useUpdateReservationStatusMutation from "api/mutations/reservations/useUpdateReservationStatus"
import useCreateWebReservationMutation from "api/mutations/reservations/useCreateWebReservation"

import { UI_INITIAL_STATE } from "providers/UIProvider/UIProviderConstants"
import { useAppContext } from "providers/AppProvider/AppProvider"
import { useCustomersContext } from "providers/CustomersProvider/CustomersProvider"
import { onMessageListener } from "../../firebase"
import { toast } from "react-toastify"
import { IRoles } from "types/shared"
import { useIndexedDB } from "messageDB"
import useGetCarWashDiscountTypes from "api/querries/reservations/useGetCarWashDiscountTypes"
import moment from "moment"
import useMarkReservationAsPrintedMutation from "api/mutations/reservations/useMarkReservationAsPrinted"
import useGetCarWashWashers from "api/querries/reservations/useGetCarWashWashers"
import useUpdateReservationWasherMutation from "api/mutations/reservations/useUpdateReservationWasher"
import useSetVehicleKeyStatus from "api/mutations/reservations/useSetVehicleKeyStatus"
import useGetCarWashBoxes from "api/querries/reservations/useGetCarWashBoxes"

export const ReservationsContext = createContext<IReservationsContext>(RESERVATIONS_INITIAL_CONTEXT)

export const useReservationsContext: () => IReservationsContext = () => useContext(ReservationsContext)

const ReservationsProvider: React.FC<IReservationsProviderProps> = ({ children }) => {
  const { search, pathname } = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(search)

  const { toggleModal, lang, modals, T } = useUI()
  // console.log(keywords["ka"]?.["dashboard.laundry"])
  const { selectedBranches, setState: setAppState, userRole, laundry } = useAppContext()
  const { currentCustomerId } = useCustomersContext()

  const isCustomersPage = pathname === "/customers"

  const [state, setState] = useReducer(
    (oldState: any, newState: any) => ({ ...oldState, ...newState }),
    RESERVATIONS_INITIAL_STATE,
  )
  const isQappUser = userRole && [IRoles.QAPP_ADMIN, IRoles.QAPP_MANAGER].includes(userRole);
  const debouncedSearchValue = useDebounce(state.searchValue, 1000)

  const formattedFromDate = state?.selectedDates ? moment(state?.selectedDates[0]).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).utc().format("YYYY-MM-DD") + "Z" : ""
  const formattedToDate = state?.selectedDates?.[1] ? moment(state?.selectedDates[1]).set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).utc().format("YYYY-MM-DD") + "Z" : ""

  const [query, setQuery] = useState<IGetReservationsQueryParams>({
    sortBy: isCustomersPage ? "id" : searchParams.get("sortBy") || "number",
    pageIndex: isCustomersPage ? 1 : Number(searchParams.get("pageIndex")) || 1,
    pageSize: isCustomersPage ? 10 : Number(searchParams.get("pageSize")) || state.pageSize?.value,
    // onGoing: isCustomersPage ? false : Boolean(searchParams.get("onGoing") === "true") || false,
    search: isCustomersPage ? "" : searchParams.get("search") || "",
    carWashIds: isCustomersPage ? [] : selectedBranches || [],
    statusIds: state?.selectedStatuses || [],
    vehicleBodyTypeIds: state?.selectedVehicleTypes,
    boxIds: state?.selectedBoxes || [],
    washerIds: state?.selectedWashers || [],
    fromDate: formattedFromDate || "",
    toDate: formattedToDate || "",
    customerId: isCustomersPage ? currentCustomerId : undefined
  })

  const fetchReservations = isCustomersPage ? query.customerId !== "" : true
  const { data: reservationsData, isFetched: reservationsDataFetched, refetch: refetchReservationsData } = useGetReservations({
    key: "reservations",
    getBoxInfo: true,
    query,
    enabled: fetchReservations
  })
  const { data: reservationData, isFetched: reservationDataFetched, refetch: refetchReservationData, isFetching: isReservationDataFetching } = useGetReservation(state.currentReservationId, Boolean(state.currentReservationId))
  const { data: carWashScheduleData, isFetched: isCarWashScheduleDataFetched } = useGetCarWashSchedule(
    (isQappUser && state.selectedBranchForReservation !== 0) || (!isQappUser && laundry?.id) ? true : false,
    state.vehicleBodyTypeId == null || undefined || 0 ? 1 : state.vehicleBodyTypeId,
    state.washTypeId == null || undefined ? 3 : state.washTypeId,
    state.selectedBranchForReservation !== 0 ? state.selectedBranchForReservation : 0);
  const { mutate: updateReservationStatus, isSuccess: updateReservationStatusSuccess } = useUpdateReservationStatusMutation()
  const { mutate: updateReservationBox, isLoading: updateReservationBoxLoading, isSuccess: updateReservationBoxSuccess } = useUpdateReservationBoxMutation()
  const { mutate: updateReservationWasher, isLoading: updateReservationWasherLoading, isSuccess: updateReservationWasherSuccess } = useUpdateReservationWasherMutation()
  const { mutate: recalculateReservationAmount, data: recalculateReservationAmountData, isLoading: recalculateReservationAmountLoading, isSuccess: recalculateReservationAmountSuccess } = useRecalculateReservationAmountMutation()
  const { mutate: updateReservationInfo, isLoading: updateReservationInfoLoading, isSuccess: updateReservationInfoSuccess, data: updateReservationData } = useUpdateReservationInfoMutation()
  const { mutate: cancelReservation, isLoading: cancelReservationLoading, isSuccess: cancelReservationSuccess } = useCancelReservationMutation()
  const { mutate: markReservationAsPrinted, isSuccess: isMarkReservationAsPrintedSuccess } = useMarkReservationAsPrintedMutation()
  const { mutate: setVehicleKeyStatus, isSuccess: isSetVehicleKeyStatusSuccess } = useSetVehicleKeyStatus()
  const { mutate: createWebReservation, isSuccess: isCreateWebReservationSuccess, isLoading: createWebReservationLoading } = useCreateWebReservationMutation()
  const updateSelectedBranchForReservation = (newBranch: number) => setState({ selectedBranchForReservation: newBranch })
  const { data: carWashWashersData } = useGetCarWashWashers((state?.selectedBranchForReservation ?
    state?.selectedBranchForReservation
    : laundry?.id) || 0, Boolean(modals.webReservationsModal.isOpened))
  const { data: carWashBoxesData } = useGetCarWashBoxes((state?.selectedBranchForReservation ?
    state?.selectedBranchForReservation
    : laundry?.id) || 0, Boolean(modals.webReservationsModal.isOpened))


  const discountId = modals.reservationDetailsModal.isOpened ? reservationData?.data?.carWashId : state?.selectedBranchForReservation
  const { data: discountTypesData } = useGetCarWashDiscountTypes(discountId, Boolean(getDiscountTypeId()))
  function getDiscountTypeId() {
    state?.selectedBranchForReservation ?
      state?.selectedBranchForReservation
      : laundry?.id || state?.currentReservation?.carWashId

    if (state?.selectedBranchForReservation) {
      return state.selectedBranchForReservation
    } else if (!state?.selectedBranchForReservation && state?.currentReservation?.carWashId
      && (modals.reservationDetailsModal.isOpened || modals.editReservationDetailsModal.isOpened)) {
      return state.currentReservation?.carWashId
    } else if (!state?.selectedBranchForReservation && laundry?.id
      && (modals.webReservationsModal.isOpened || modals.webReservationsTimesModal.isOpened)
    ) {
      return laundry.id
    } else {
      return false
    }
  }


  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        const messages: any = await retrieveMessages();
        if (messages.length !== 0) {
          refetchReservationsData()
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Clean up the event listener when the component unmounts.
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

  }, []);


  function retrieveMessages() {
    return new Promise(function (resolve, reject) {
      useIndexedDB().createOrGetIndexedDB()
      const request = indexedDB.open('qapp-firebase-messages', 1);
      request.onsuccess = (event: any) => {
        const db = event.target.result;
        const tx = db.transaction('messages', 'readwrite');
        const store = tx.objectStore('messages');
        const messages: number[] = [];

        store.openCursor().onsuccess = async function (event: any) {
          const cursor = event.target.result;
          if (cursor) {
            const copy: any = { ...cursor.value }
            messages.push(copy);
            // await cursor.delete();
            cursor.continue();
          } else {
            resolve(messages as any[]);
          }
        };
      };
    });
  }

  async function onForeground() {
    const messages: any = await retrieveMessages();
    const reservationIds: number[] = messages.filter((e: any) => e.MessageType === "8").map((e: any) => parseInt(e.ReservationId))
    setAppState({ reservationIdsToUpdate: reservationIds })
  }

  onMessageListener().then(payload => {
    const { data } = payload
    if ((data.MessageType === NOTIFICATION_MESSAGE_TYPES.RESERVATIONS_UPDATE || data.MessageType === NOTIFICATION_MESSAGE_TYPES.ADMIN_CARONPLACE_PUSH) && pathname === "/reservations") {
      setState({ selectedStatuses: [] })
      refetchReservationsData()
    }
    onForeground()
  })

  const updateSearchParams = (query: any) => {
    navigate({
      search: `?${new URLSearchParams({
        ...query
      }).toString()}`
    })
  }

  const handleReservationsPageChange = (newPage: number) => {
    const updatedQuery = {
      ...query,
      pageIndex: newPage
    }
    updateSearchParams(updatedQuery)
    setQuery(updatedQuery)
  }



  const handleSelectDatesClick = (dates: Date[]) => {
    setState({
      selectedDates: dates
    })
  }

  const handleClearAllFilters = () => {
    const updatedQuery = {
      ...query,
      fromDate: "",
      toDate: "",
      statusIds: [],
      vehicleBodyTypeIds: [],
      washerIds: [],
      boxIds: []
    }
    setQuery(updatedQuery)
    setState({
      selectedStatuses: [],
      selectedVehicleTypes: [],
      selectedBoxes: [],
      selectedWashers: [],
      selectedDates: null
    })
    updateSearchParams("")
  }

  const handleStatusCheckboxClick = (statusId: number) => {
    let updatedSelectedStatuses = [...state.selectedStatuses]


    if (updatedSelectedStatuses.includes(statusId)) {
      updatedSelectedStatuses = updatedSelectedStatuses.filter((selectedStatus: number) => selectedStatus !== statusId)
    } else {
      updatedSelectedStatuses.push(statusId)
    }

    setState({ selectedStatuses: updatedSelectedStatuses })
  }

  const handleVehicleTypeCheckBox = (vehicleId: number) => {
    let updatedVehicleTypes = [...state.selectedVehicleTypes]


    if (updatedVehicleTypes.includes(vehicleId)) {
      updatedVehicleTypes = updatedVehicleTypes.filter((selectedVehicleType: number) => selectedVehicleType !== vehicleId)
    } else {
      updatedVehicleTypes.push(vehicleId)
    }

    setState({ selectedVehicleTypes: updatedVehicleTypes })
  }

  const handleBoxCountCheckbox = (box: number) => {
    let updatedBoxes = [...state.selectedBoxes]


    if (updatedBoxes.includes(box)) {
      updatedBoxes = updatedBoxes.filter((selectedBox: number) => selectedBox !== box)
    } else {
      updatedBoxes.push(box)
    }

    setState({ selectedBoxes: updatedBoxes })
  }

  const handleWashersFilter = (washer: number) => {
    let updatedWashers = [...state.selectedWashers]


    if (updatedWashers.includes(washer)) {
      updatedWashers = updatedWashers.filter((selectedWasherId: number) => selectedWasherId !== washer)
    } else {
      updatedWashers.push(washer)
    }

    setState({ selectedWashers: updatedWashers })
  }



  const handleSearchChange = (val: string) => setState({ searchValue: val })

  const handleBoxSelectChange = (reservationId: number, box: number) => {
    updateReservationBox({ reservationId, box })
  }

  const handleWasherSelectChange = (reservationId: number, employeeId: number) => {
    updateReservationWasher({ reservationId, employeeId })
  }

  const handleViewReservationClick = (reservationId: number) => {
    setState({ currentReservationId: reservationId })
  }

  const handleCancelReservationClick = (reservationId: number) => {
    setState({ currentReservationId: reservationId })
  }

  const handleChangeReservationStatusClick = (reservationId: number) => {
    setState({ currentReservationId: reservationId })
  }

  const handleRecalculateReservationAmount = (vehicleId: number, washTypeId: number, vehicleBodyTypeId: number) => {
    recalculateReservationAmount({ reservationId: state.currentReservation?.id, personVehicleId: vehicleId, washTypeId, vehicleBodyTypeId })
  }

  // const handleUpdateReservationInfo = (vehicleId: number, washTypeId: number, vehicleBodyTypeId: number) => {
  //   updateReservationInfo({ reservationId: state.currentReservation?.id, personVehicleId: vehicleId, washTypeId, vehicleBodyTypeId })
  // }

  const handleCancelReservation = (cancelBy: number) => {
    cancelReservation({ reservationId: state.currentReservationId, cancelBy })
  }


  const handleMarkReservationAsPrinted = (reservationId: number) => {
    markReservationAsPrinted(reservationId)
  }

  const handleSetVehicleKeyStatus = (reservationId: number) => {
    setVehicleKeyStatus(reservationId)
  }

  const handleTablePageSize = (pageSizeNumber: number) => {

    setState({ pageSize: pageSizeNumber })
  }

  useEffect(() => {
    if (updateReservationStatusSuccess) {
      refetchReservationData()
    }
  }, [updateReservationStatusSuccess])

  useEffect(() => {
    if (reservationsData && reservationsDataFetched) {
      const { reservations, statuseGroups, availableBoxCount, reservedBoxCount, boxCount, vehicleBodyTypeGroups, boxData, washersForFilter } = reservationsData.data
      setState({
        reservations,
        statusGroups: statuseGroups,
        washersForFilter: washersForFilter,
        boxCount: boxCount,
        vehicleBodyTypeGroups: vehicleBodyTypeGroups,
        boxTotals: {
          availableBoxCount,
          reservedBoxCount
        },
        reservationsData: reservationsData,
        boxData: boxData
      })
    }
  }, [reservationsData])


  useEffect(() => {
    setState({ discountTypesData: discountTypesData?.data })
  }, [discountTypesData])

  useEffect(() => {
    setState({ carWashWashers: carWashWashersData?.data })
  }, [carWashWashersData])

  useEffect(() => {
    setState({ carWashBoxes: carWashBoxesData?.data })
  }, [carWashBoxesData])


  useEffect(() => {
    if (debouncedSearchValue !== undefined) {
      const udpatedQuery = {
        ...query,
        search: debouncedSearchValue || "",
        pageIndex: 1,
      }
      updateSearchParams(udpatedQuery)
      setQuery(udpatedQuery)
    }
  }, [debouncedSearchValue])

  useEffect(() => {
    if (updateReservationBoxSuccess) {
      refetchReservationsData()
    }

  }, [updateReservationBoxSuccess])

  useEffect(() => {
    if (updateReservationWasherSuccess) {
      refetchReservationsData()
    }

  }, [updateReservationWasherSuccess])

  useEffect(() => {
    if (!isCustomersPage) {
      refetchReservationsData()
    }
  }, [modals.carOnPlaceModal.isOpened])

  useEffect(() => {
    if (carWashScheduleData && isCarWashScheduleDataFetched) {
      setState({ carWashScheduleData: carWashScheduleData.data })
    }
  }, [carWashScheduleData])

  useEffect(() => {
    if (reservationData && reservationDataFetched) {
      setState({ currentReservation: reservationData.data })
    }
  }, [reservationData])

  useEffect(() => {
    if (state.currentReservationId) {
      refetchReservationData()
    }
  }, [state.currentReservationId])

  useEffect(() => {
    if (recalculateReservationAmountSuccess) {
      setState({ modifiedPrice: recalculateReservationAmountData.data })
    }
  }, [recalculateReservationAmountSuccess])

  useEffect(() => {
    if (!isCustomersPage) {
      if (updateReservationData?.status === 200) {
        refetchReservationsData()
        refetchReservationData()
        toggleModal("editReservationDetailsModal", false)
        toast.success(T("bookings.edit.details.successfully.changed"))
      }
      if (updateReservationData?.errStatus) {
        toast.error(updateReservationData?.err)
      }
    }
  }, [updateReservationInfoSuccess])

  useEffect(() => {
    if (!isCustomersPage) {
      refetchReservationsData()
      toggleModal("reservationDetailsModal", false)
    }
  }, [isMarkReservationAsPrintedSuccess])

  useEffect(() => {
    if (!isCustomersPage) {
      refetchReservationsData()
      toggleModal("reservationDetailsModal", false)
    }
  }, [isSetVehicleKeyStatusSuccess])


  useEffect(() => {
    if (cancelReservationSuccess) {
      refetchReservationsData()
      toggleModal("cancelReservationModal", false)
    }
  }, [cancelReservationSuccess])

  useEffect(() => {
    if (updateReservationStatusSuccess) {
      refetchReservationsData()
      toggleModal("confirmationModal", false, UI_INITIAL_STATE.modals.confirmationModal.data)
    }
  }, [updateReservationStatusSuccess])

  useEffect(() => {
    if (isCreateWebReservationSuccess) {
      refetchReservationsData()
      toggleModal("webReservationsTimesModal", false)
      toggleModal("webReservationsModal", false)
      toast.success(T("bookings.successfully.added"))
      setState({ selectedBranchForReservation: 0 })
    }
  }, [isCreateWebReservationSuccess])


  useEffect(() => {
    if (lang && reservationsDataFetched) {
      refetchReservationsData()
    }
  }, [lang])



  useEffect(() => {
    if (state?.selectedStatuses !== undefined) {
      setQuery({
        ...query,
        statusIds: state?.selectedStatuses
      })
    }
  }, [state?.selectedStatuses])

  useEffect(() => {
    if (state?.selectedVehicleTypes !== undefined) {
      setQuery({
        ...query,
        vehicleBodyTypeIds: state?.selectedVehicleTypes
      })
    }
  }, [state?.selectedVehicleTypes])

  useEffect(() => {
    if (state?.selectedBoxes !== undefined) {
      setQuery({
        ...query,
        boxIds: state?.selectedBoxes
      })
    }
  }, [state?.selectedBoxes])

  useEffect(() => {
    if (state?.selectedWashers !== undefined) {
      setQuery({
        ...query,
        washerIds: state?.selectedWashers
      })
    }
  }, [state?.selectedWashers])


  useEffect(() => {
    if (state?.selectedDates !== undefined) {
      if (state?.selectedDates?.[1]) {
        setQuery({
          ...query,
          fromDate: formattedFromDate,
          toDate: formattedToDate
        })
      }
    }
  }, [state?.selectedDates])

  useEffect(() => {
    if (selectedBranches !== undefined && !isCustomersPage) {
      setQuery({
        ...query,
        carWashIds: selectedBranches
      })
    }
  }, [selectedBranches])

  // useEffect(() => {
  //   handleClearAllFilters()
  // }, [query.customerId])



  useEffect(() => {
    if (selectedBranches !== undefined && !isCustomersPage) {
      setQuery({
        ...query,
        pageSize: state?.pageSize?.value
      })
    }
  }, [state?.pageSize?.value])


  useEffect(() => {
    if (isCustomersPage) {
      if (currentCustomerId !== undefined) {
        setQuery({
          ...query,
          customerId: currentCustomerId
        })
      }
    }
  }, [currentCustomerId])


  const value = useMemo(() => ({
    ...state,
    updateReservationStatus,
    setState,
    query,
    updateReservationBoxLoading,
    isReservationDataFetching,
    updateReservationWasherLoading,
    recalculateReservationAmountLoading,
    updateReservationInfoLoading,
    createWebReservationLoading,
    cancelReservationLoading,
    handleReservationsPageChange,
    handleMarkReservationAsPrinted,
    handleSetVehicleKeyStatus,
    handleClearAllFilters,
    handleStatusCheckboxClick,
    handleSelectDatesClick,
    handleVehicleTypeCheckBox,
    handleBoxCountCheckbox,
    handleWashersFilter,
    handleCancelReservationClick,
    handleChangeReservationStatusClick,
    handleSearchChange,
    handleBoxSelectChange,
    handleWasherSelectChange,
    handleViewReservationClick,
    handleRecalculateReservationAmount,
    handleCancelReservation,
    updateSelectedBranchForReservation,
    createWebReservation,
    handleTablePageSize,
    updateReservationInfo,
    carWashScheduleData
  }), [state, query, updateReservationBoxLoading, updateReservationWasherLoading, recalculateReservationAmountLoading, updateReservationInfoLoading, createWebReservationLoading, cancelReservationLoading, currentCustomerId])

  return <ReservationsContext.Provider value={value}>
    {children}
  </ReservationsContext.Provider>
}

export default ReservationsProvider